import { Button, CardContent, CardHeader, Collapse, Divider, Grid, Typography } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useId, useMemo, useState } from "react";

interface CollapsibleParticipantsListProps {
    benefitients: string;
}

type OrderParticipant = string[];

const parseOrderParticipants = (benefitients: string): OrderParticipant[] =>
    benefitients
        .split(';')
        .map(participant => participant.trim())
        .filter(Boolean)
        .map(participant => participant.split('~').map(value => value.trim()));

interface ParticipantsListProps {
    participants: OrderParticipant[];
}

export const ParticipantsList: React.FC<ParticipantsListProps> = ({ participants }) => {
    if (participants.length === 0) return null;

    return (
        <Grid container spacing={2}>
            {participants.map((participant, index) => (
                <Grid item xs={12} key={index}>
                    <Typography variant="h6">{`Uczestnik ${index + 1}:`}</Typography>
                    <Typography variant="body1" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                        {participant.filter(Boolean).join(' ')}
                    </Typography>
                </Grid>
            ))}
        </Grid>
    );
};

export const CollapsibleParticipantsList: React.FC<CollapsibleParticipantsListProps> = ({ benefitients }) => {
    const [expanded, setExpanded] = useState(false);
    const participants = useMemo(() => parseOrderParticipants(benefitients), [benefitients]);
    const contentId = useId();

    useEffect(() => {
        setExpanded(false);
    }, [benefitients]);

    return (
        <>
            <CardHeader
                title={`Lista uczestników (${participants.length})`}
                titleTypographyProps={{
                    sx: { fontWeight: 'bold', color: 'secondary' }
                }}
                action={
                    <Button
                        type="button"
                        variant="text"
                        disableElevation
                        disableRipple
                        aria-controls={contentId}
                        aria-expanded={expanded}
                        endIcon={
                            <ExpandMoreIcon
                                sx={{
                                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: theme => theme.transitions.create('transform', {
                                        duration: theme.transitions.duration.shortest,
                                    }),
                                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                }}
                            />
                        }
                        onClick={() => setExpanded(current => !current)}
                        sx={{
                            minHeight: 44,
                            minWidth: 0,
                            px: 0,
                            border: 0,
                            borderRadius: 0,
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                textDecoration: 'underline',
                            },
                            '&:active': {
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                opacity: 0.7,
                            },
                            '&:focus': {
                                boxShadow: 'none',
                            },
                            '&.Mui-focusVisible': {
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                outline: 'none',
                                textDecoration: 'underline',
                                textDecorationThickness: '2px',
                                textUnderlineOffset: '4px',
                            },
                        }}
                    >
                        {expanded ? 'Ukryj listę' : 'Pokaż listę'}
                    </Button>
                }
            />
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Divider />
                <CardContent id={contentId}>
                    <ParticipantsList participants={participants} />
                </CardContent>
            </Collapse>
        </>
    );
};
